import { Check } from 'lucide-react';
import * as React from 'react';
import {
  ListBox as AriaListBox,
  ListBoxItem as AriaListBoxItem,
  type ListBoxProps as AriaListBoxProps,
  type ListBoxItemProps as AriaListBoxItemProps
} from 'react-aria-components';
import { cn } from 'v5/platform/dom/cn';
import { Skeleton } from './Skeleton';

// ------------------------
// LISTBOX COMPONENT
// ------------------------

export interface ListBoxProps extends AriaListBoxProps<object> {
  className?: string;
}

export const ListBox = React.forwardRef<
  React.ElementRef<typeof AriaListBox>,
  ListBoxProps
>(({ className, ...props }, ref) => {
  return (
    <AriaListBox
      ref={ref}
      className={cn('divide-y divide-border-default isolate', className)}
      {...props}
    />
  );
});
ListBox.displayName = 'ListBox';

// ------------------------
// LISTBOX ITEM COMPONENT
// ------------------------

export interface ListBoxItemProps extends AriaListBoxItemProps {
  className?: string;
}

export const ListBoxItem = React.forwardRef<
  React.ElementRef<typeof AriaListBoxItem>,
  ListBoxItemProps
>(({ className, ...props }, ref) => {
  return (
    <AriaListBoxItem
      ref={ref}
      className={cn(
        'flex items-center gap-4 p-4 transition-all cursor-pointer group outline-none',
        'last:rounded-b-lg',
        'hover:bg-bg-subtle',
        'aria-selected:bg-bg-subtle',
        'focus-visible:ring-1 focus-visible:ring-brand-300 focus-visible:ring-inset',
        className
      )}
      {...props}
    />
  );
});
ListBoxItem.displayName = 'ListBoxItem';

// ------------------------
// LISTBOX ITEM CONTENT LAYOUTS
// ------------------------

export interface ListBoxItemContentProps {
  icon?: React.ReactNode;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  extra?: React.ReactNode;
  showCheckmark?: boolean;
  className?: string;
}

/**
 * Standard list item content layout with icon, primary text, secondary text,
 * extra content (like badges/dates), and optional checkmark
 */
export const ListBoxItemContent = React.forwardRef<
  HTMLDivElement,
  ListBoxItemContentProps
>(
  (
    {
      icon,
      primary,
      secondary,
      extra,
      showCheckmark = true,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <>
        {icon && (
          <div className="shrink-0 flex items-center" aria-hidden="true">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0 flex flex-col" ref={ref} {...props}>
          <div className="font-medium leading-snug truncate">{primary}</div>
          {secondary && (
            <div className="text-sm text-fg-muted mt-0.5 truncate">
              {secondary}
            </div>
          )}
        </div>
        {extra && <div className="shrink-0 flex items-center">{extra}</div>}
        {showCheckmark && (
          <div className="shrink-0 flex items-center">
            <div className="size-6 rounded-full border-2 border-border-default group-hover:border-charcoal-light/50 group-aria-selected:bg-charcoal-light group-aria-selected:border-charcoal-light flex items-center justify-center transition-all">
              <Check className="size-4 text-white opacity-0 group-aria-selected:opacity-100 transition-opacity" />
            </div>
          </div>
        )}
      </>
    );
  }
);
ListBoxItemContent.displayName = 'ListBoxItemContent';

/**
 * Compact list item content layout for simpler items
 */
export const ListBoxItemContentCompact = React.forwardRef<
  HTMLDivElement,
  Omit<ListBoxItemContentProps, 'secondary'>
>(
  (
    { icon, primary, extra, showCheckmark = true, className, ...props },
    ref
  ) => {
    return (
      <>
        {icon && (
          <div className="shrink-0 flex items-center" aria-hidden="true">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0" ref={ref} {...props}>
          <div className="font-medium leading-snug truncate">{primary}</div>
        </div>
        {extra && <div className="shrink-0 flex items-center">{extra}</div>}
        {showCheckmark && (
          <div className="shrink-0 flex items-center">
            <div className="size-6 rounded-full border-2 border-border-default group-hover:border-charcoal-light/50 group-aria-selected:bg-charcoal-light group-aria-selected:border-charcoal-light flex items-center justify-center transition-all">
              <Check className="size-4 text-white opacity-0 group-aria-selected:opacity-100 transition-opacity" />
            </div>
          </div>
        )}
      </>
    );
  }
);
ListBoxItemContentCompact.displayName = 'ListBoxItemContentCompact';

// ------------------------
// LISTBOX HEADER COMPONENT
// ------------------------

export interface ListBoxHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Header component for ListBox that appears above the list items.
 * Typically used for selection info, actions, or other list metadata.
 */
export const ListBoxHeader = React.forwardRef<
  HTMLDivElement,
  ListBoxHeaderProps
>(({ className, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-between border-b border-border-default p-3 shrink-0',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
ListBoxHeader.displayName = 'ListBoxHeader';

// ------------------------
// LISTBOX SKELETON COMPONENT
// ------------------------

export interface ListBoxSkeletonProps {
  numRows?: number;
  showIcon?: boolean;
  showSecondary?: boolean;
  showExtra?: boolean;
  showCheckmark?: boolean;
  className?: string;
}

/**
 * Skeleton loading state for ListBox component
 * Matches the structure of ListBoxItem with ListBoxItemContent
 */
export function ListBoxSkeleton({
  numRows = 4,
  showIcon = true,
  showSecondary = true,
  showExtra = true,
  showCheckmark = true,
  className
}: ListBoxSkeletonProps) {
  return (
    <div className={cn('divide-y divide-border-default', className)}>
      {Array.from({ length: numRows }).map((_, index) => (
        <div
          key={index}
          className="flex items-center gap-4 p-4"
          role="presentation"
          aria-hidden="true"
        >
          {showIcon && (
            <div className="shrink-0 flex items-center">
              <Skeleton className="size-5 rounded" />
            </div>
          )}
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <Skeleton className="h-5 w-48" />
            {showSecondary && <Skeleton className="h-4 w-32" />}
          </div>
          {showExtra && (
            <div className="shrink-0 flex items-center gap-3">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
          )}
          {showCheckmark && (
            <div className="shrink-0 flex items-center">
              <Skeleton className="size-6 rounded-full" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
ListBoxSkeleton.displayName = 'ListBoxSkeleton';
